import { Component, effect, inject, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { AsyncPipe, NgClass, DatePipe } from '@angular/common';
import { IconComponent, Pagination } from '../../../../shared';
import { UsersFilter, UsersSortType } from '../../interfaces/user-list.interfaces';
import { UserService } from '../../services/user.service';
import { SchemaMembershipStateService } from 'libs/core/state/schema-state.service';
import { UserUiService } from '../../services/user-ui.service';
import { UserRole, UserStatus } from 'libs/domain/user/user-enums';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommunityUser, PaginatedResult } from 'libs/domain';

@Component({
  selector: 'lib-user-list',
  imports: [NgClass, AsyncPipe, DatePipe, Pagination, IconComponent, RouterModule],
  templateUrl: './user-list.html'
})


export class UserListComponent {
  private schemaState = inject(SchemaMembershipStateService);
  private userService = inject(UserService);
  private userUiService = inject(UserUiService);
  router = inject(Router);
  route = inject(ActivatedRoute);

  schema = this.schemaState.schema;
  userRoles = UserRole;
  userStatus = UserStatus;

  totalPages: number = 0;
  currentPage: number = 0;

  users: WritableSignal<CommunityUser[]> = signal([])

  pageSize: Number = 12;
  filter: UsersFilter = {
    searchTerm: '',
    sortBy: '',
    sortDirection: UsersSortType.NONE,
  };
  search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  loaded: WritableSignal<boolean> = signal(false);
  loadedWithError: WritableSignal<boolean> = signal(false);

  searchTerm = '';

   constructor(){
    effect(() => {
      if(!this.schema()) return;
      this.loadUsers();
      })
  }

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term) => {
        this.searchTerm = term;
        this.filter.searchTerm = term || undefined;

        // reset paginació
        this.currentPage = 0;

        this.loadUsers(); // fa la petició
      });

    this.currentPage = 0;
    this.loadUsers();
  }

  loadUsers() {
    this.userService.getUsersPage(this.schema()?.schema_id!, this.currentPage, this.pageSize, this.filter).subscribe({
      next:((page: PaginatedResult<CommunityUser>) => {
        this.currentPage = page.currentPage;
        this.totalPages = page.totalPages;
        this.users.set(page.results);
      }),
      error: () => {this.loaded.set(true);this.loadedWithError.set(true);},
      complete: () => { this.loaded.set(true); }
    })
  }

  viewUser(user: any): void {
    this.router.navigate([user.id], {
      relativeTo: this.route,
      state: { user }
    });
  }

  roleLabel(role: UserRole): string {
    return this.userUiService.getUserRoleString(role);
  }

  statusLabel(status: UserStatus): string {
    return this.userUiService.getUserStatusString(status);
  }

  
  onSearchInput(value: string): void {
    this.search$.next(value);
  }

  onSortClick(key: string): void {
    if (this.filter.sortBy !== key) {
      this.filter.sortBy = key;
      this.filter.sortDirection = UsersSortType.ASCENDING;
    } else {
      this.filter.sortDirection =
        this.filter.sortDirection === UsersSortType.NONE ? UsersSortType.ASCENDING :
          this.filter.sortDirection === UsersSortType.ASCENDING ? UsersSortType.DESCENDING :
            UsersSortType.NONE;

      if (this.filter.sortDirection === UsersSortType.NONE) {
        this.filter.sortBy = undefined;
      }
    }

    this.currentPage = 0;
    this.fetch();
  }

  fetch() {
    this.loadUsers();
  }

  sortIconClass(key: string, ascIcon: boolean): Record<string, boolean> {
    const dir = this.filter?.sortDirection;
    const active =
      this.filter?.sortBy === key &&
      (ascIcon ? dir === UsersSortType.ASCENDING : dir === UsersSortType.DESCENDING);

    return {
      'text-gray-500 dark:text-gray-400': !!active,
      'text-gray-300 dark:text-gray-400/50': !active,
    };
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  getRoleBadgeClass(role: UserRole): string {
    return this.userUiService.getUserRoleBadgeClass(role);
  }

  getStatusBadgeClass(status: UserStatus): string {
    return this.userUiService.getUserStatusBadgeClass(status);
  }

  getIsEmailValidBadgeClass(): string {
    return this.userUiService.getUserIsEmailValidBadgeClass();
  }
}
